import { ICollectionPromo } from "@faire/web-api/indigofair/data/ICollectionPromo";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { IProductBasedCollectionMetadata } from "@faire/web-api/indigofair/data/IProductBasedCollectionMetadata";
import { IRetailerProductBasedCollectionResponse } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionResponse";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import * as React from "react";
import styled from "styled-components";

import { ProductBasedCollectionPageWrapper } from "@faire/retailer/views/product-based-collection/__internal__/__internal__/ProductBasedCollectionPageWrapper";
import {
  DESKTOP_COLUMN_COUNT,
  MOBILE_COLUMN_COUNT,
  TABLET_COLUMN_COUNT,
} from "@faire/retailer/views/product-based-collection/__internal__/__internal__/utils";
import { CollectionHeader } from "@faire/retailer/views/product-based-collection/__internal__/CollectionHeader";
import { OtherProductBasedCollections } from "@faire/retailer/views/product-based-collection/__internal__/OtherProductBasedCollections";

import { PBCEfficientBuyingItemList } from "./CollectionDiscovery/PBCEfficientBuyingItemList";

interface IProductBasedCollectionPageContentBaseProps {
  collection: IRetailerProductBasedCollectionResponse.IRetailerProductBasedCollection;
  productTiles: ILeanProductTile[];
  otherCollections: IProductBasedCollectionMetadata[];
  showVerticalImagery?: boolean;
  collectionPromo?: ICollectionPromo;
}

export const ProductBasedCollectionPageContent: React.FC<
  IProductBasedCollectionPageContentBaseProps
> = ({
  collection,
  productTiles,
  otherCollections,
  showVerticalImagery,
  collectionPromo,
}) => {
  const gridGap = useResponsiveValue({
    mobileAndAbove: 8,
    tabletAndAbove: 16,
  });

  const columnCount = useResponsiveValue({
    mobileAndAbove: MOBILE_COLUMN_COUNT,
    tabletAndAbove: TABLET_COLUMN_COUNT,
    desktopAndAbove: DESKTOP_COLUMN_COUNT,
  });

  return (
    <Column>
      <Spacer height={{ mobileAndAbove: 0, tabletAndAbove: "6x" }} />

      <ProductBasedCollectionPageWrapper>
        <CollectionHeader
          collection={collection}
          collectionPromo={collectionPromo}
        />
        <Spacer height="5x" />
        <StyledColumn>
          <PBCEfficientBuyingItemList
            collection={collection}
            productTiles={productTiles}
            showVerticalImagery={showVerticalImagery}
            columnCount={columnCount}
            gridGap={gridGap}
            collectionPromo={collectionPromo}
          />
        </StyledColumn>
        {otherCollections.length ? (
          <StyledColumn>
            <Spacer height={{ mobileAndAbove: "5x", desktopAndAbove: "6x" }} />
            <OtherProductBasedCollections
              collection={collection}
              otherCollections={otherCollections}
              columnCount={columnCount}
              gridGap={gridGap}
            />
          </StyledColumn>
        ) : null}
        <Spacer height={{ mobileAndAbove: "5x", desktopAndAbove: "6x" }} />
      </ProductBasedCollectionPageWrapper>
    </Column>
  );
};

const StyledColumn = styled(Column)`
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(0, "2x")};
  }
`;
